import * as React from 'react';

type AnyRef = React.RefObject<HTMLElement | null>;

/** Close-on-outside-click for popovers. Shared by the top bar's dropdowns so
 *  they dismiss identically. `enabled` is usually the open flag — the listener
 *  isn't attached while the popover is closed.
 *
 *  Takes several refs because a portaled menu lives outside its trigger's
 *  subtree: without listing it here, mousedown inside the menu would count as
 *  outside and unmount it before the click on the item ever landed. */
export function useOutsideClick(refs: AnyRef | AnyRef[], onOutside: () => void, enabled: boolean) {
  const list = React.useMemo(() => (Array.isArray(refs) ? refs : [refs]), [refs]);

  React.useEffect(() => {
    if (!enabled) return undefined;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const inside = list.some((ref) => ref.current?.contains(target));
      if (!inside) onOutside();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [list, onOutside, enabled]);
}

export default useOutsideClick;
